import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createCommissionFormula,
  createCommissionRule,
  deleteCommissionFormula,
  deleteCommissionRule,
  deleteCoefficientTable,
  getCoefficientTables,
  getCommissionFormulas,
  getCommissionRules,
  saveCoefficientTable,
  testCommissionFormula,
  calculatePersonnelCommission,
} from "./commissions";
import { getManufacturersList } from "../../reference/api/catalog";
import { getStaffList } from "../../staff/api/staff";
import { commissionsQueryKeys } from "./query-keys";

export function useGetCommissionRules() {
  return useQuery({
    queryKey: commissionsQueryKeys.rules(),
    queryFn: getCommissionRules,
  });
}

export function useGetCommissionFormulas() {
  return useQuery({
    queryKey: commissionsQueryKeys.formulas(),
    queryFn: getCommissionFormulas,
  });
}

export function useGetCoefficientTables() {
  return useQuery({
    queryKey: commissionsQueryKeys.coefficientTables(),
    queryFn: getCoefficientTables,
  });
}

export function useCreateCommissionRuleMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createCommissionRule,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: commissionsQueryKeys.rules() });
    },
  });
}

export function useDeleteCommissionRuleMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ staffCode, manufacturerCode }) =>
      deleteCommissionRule(staffCode, manufacturerCode),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: commissionsQueryKeys.rules() });
    },
  });
}

export function useCreateCommissionFormulaMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createCommissionFormula,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: commissionsQueryKeys.formulas() });
    },
  });
}

export function useDeleteCommissionFormulaMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteCommissionFormula,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: commissionsQueryKeys.formulas() });
    },
  });
}

export function useTestCommissionFormulaMutation() {
  return useMutation({
    mutationFn: testCommissionFormula,
  });
}

export function useSaveCoefficientTableMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: saveCoefficientTable,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: commissionsQueryKeys.coefficientTables(),
      });
    },
  });
}

export function useDeleteCoefficientTableMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteCoefficientTable,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: commissionsQueryKeys.coefficientTables(),
      });
    },
  });
}

export function useCalculatePersonnelCommissionMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: calculatePersonnelCommission,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: commissionsQueryKeys.personnel(),
      });
    },
  });
}

export function useGetCommissionRulesPageData() {
  const rules = useGetCommissionRules();
  const manufacturers = useQuery({
    queryKey: ["catalog", "manufacturers-list"],
    queryFn: getManufacturersList,
  });
  const staff = useQuery({
    queryKey: ["staff", "list"],
    queryFn: getStaffList,
  });

  return {
    rules,
    manufacturers,
    staff,
    isLoading: rules.isLoading || manufacturers.isLoading || staff.isLoading,
  };
}
